import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from 'react-i18next';
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { trpc } from "@/lib/trpc";
import { AlertCircle, Building2, Lock, User } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

/**
 * Helper function to get company size label
 */
function getCompanySizeLabel(size: string): string {
  const labels: Record<string, string> = {
    'TPE': '< 10 employees',
    'PME': '10-250 employees',
    'ETI': '250-5000 employees',
    'GE': '> 5000 employees',
  };
  return labels[size] || size;
}

/**
 * MyPA Settings Page
 * User profile and preferences management
 */
export default function Settings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: profile, refetch: refetchProfile } = trpc.profile.get.useQuery();
  const { data: company, isLoading: loadingCompany } = trpc.company.get.useQuery();
  
  // Load job details to display readable activity name
  // Note: job_code is stored in companies.activity, not user_profiles.job_code
  const { data: jobDetails } = trpc.jobs.getByCode.useQuery(
    { jobCode: company?.activity || "" },
    { enabled: !!company?.activity }
  );

  const [firstName, setFirstName] = useState(profile?.first_name || "");
  const [lastName, setLastName] = useState(profile?.last_name || "");
  const [phone, setPhone] = useState(profile?.phone || "");

  // Update mutation
  const updateProfileMutation = trpc.profile.update.useMutation({
    onSuccess: () => {
      toast.success(t('settings.profile_updated_successfully'));
      refetchProfile();
    },
    onError: (error) => {
      toast.error(`Failed to update profile: ${error.message}`);
    },
  });

  const handleSaveChanges = () => {
    if (profile?.is_locked && (firstName !== profile.first_name || lastName !== profile.last_name)) {
      toast.error(t('settings.name_fields_locked'));
      return;
    }

    updateProfileMutation.mutate({
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      phone: phone || undefined,
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('settings.title')}</h1>
          <p className="text-muted-foreground">
            {t('settings.subtitle')}
          </p>
        </div>

        <Tabs defaultValue="profile" className="w-full">
          <TabsList>
            <TabsTrigger value="profile">{t('settings.profile')}</TabsTrigger>
            <TabsTrigger value="company">{t('settings.company')}</TabsTrigger>
            <TabsTrigger value="preferences">{t('settings.preferences')}</TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-4 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  {t('settings.personal_information')}
                </CardTitle>
                <CardDescription>
                  {t('settings.update_personal_details')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={user?.email || ""}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">
                    Email cannot be changed
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      disabled={profile?.is_locked || false}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      disabled={profile?.is_locked || false}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Phone number can be updated anytime
                  </p>
                </div>

                {profile?.is_locked && (
                  <div className="flex items-start gap-2 p-3 bg-muted rounded-lg">
                    <Lock className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">Name and Email Locked</p>
                      <p className="text-xs text-muted-foreground">
                        Your name and email are locked for security. Contact a MyPA administrator to
                        unlock them if you need to make changes. Phone number remains editable.
                      </p>
                    </div>
                  </div>
                )}

                <Button
                  className="bg-gradient-mypa hover:opacity-90"
                  onClick={handleSaveChanges}
                  disabled={updateProfileMutation.isPending}
                >
                  {updateProfileMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
                <p className="text-xs text-muted-foreground">
                  {profile?.is_locked ? "Only phone number can be updated" : "Update your personal information"}
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Company Tab */}
          <TabsContent value="company" className="space-y-4 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Company Information
                </CardTitle>
                <CardDescription>
                  View your company details
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input
                    value={loadingCompany ? "Loading..." : (company?.name || "No company assigned")}
                    disabled
                    className="bg-muted"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Sector</Label>
                    <Input
                      value={company?.sector || ""}
                      disabled
                      className="bg-muted"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Input
                      value={profile?.country || ""}
                      disabled
                      className="bg-muted"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Company Size</Label>
                    <Input
                      value={company?.company_size ? `${company.company_size} - ${getCompanySizeLabel(company.company_size)}` : "Not set"}
                      disabled
                      className="bg-muted"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Job / Activity</Label>
                  <Input
                    value={jobDetails?.subsector_en || company?.activity || ""}
                    disabled
                    className="bg-muted"
                  />
                </div>

                <Separator />

                <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-900">
                  <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-500 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                      Company details are locked
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      These fields were set during profile creation and cannot
                      be modified. Contact support if you need to update them.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Preferences Tab */}
          <TabsContent value="preferences" className="space-y-4 mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Language & Region</CardTitle>
                <CardDescription>
                  Set your language and regional preferences
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="language">Language</Label>
                  <Input
                    id="language"
                    value="English"
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">
                    Multi-language support coming soon (EN/FR/AR)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Input
                    id="timezone"
                    value="Africa/Dakar (GMT+0)"
                    disabled
                    className="bg-muted"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Notifications</CardTitle>
                <CardDescription>
                  Manage your notification preferences
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Notification settings will be available in a future update.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

